import { View, Text, ScrollView } from 'react-native'
import React from 'react'
import { StyleSheet } from 'react-native-unistyles'
import Unicon from '@/components/General/Unicon'
import CutCard from '@/components/General/CutCard'
import { useRouter } from 'expo-router'
import { Query } from '@/utils/react-query/Queries'
import usePlatform from '@/utils/hooks/usePlatform'
import { MILESTONES_ENABLED } from '@/config/milestones'
import { canViewMilestoneManagement } from '@/utils/milestonePermissions'
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements'
import { isFreeOrg } from '@/utils/organizationEntitlements'

function DashboardCard({ title, description, icon, iconLibrary = 'ionicon', onPress, testID }) {
    return (
        <CutCard onPress={onPress} testID={testID} accentColor="primary" style={styles.card}>
            <View style={styles.cardHeader}>
                <View style={styles.iconContainer}>
                    <Unicon name={icon} library={iconLibrary} colorVariant="onPrimary" size={20} />
                </View>
                <View style={styles.cardTitleRow}>
                    <Text style={styles.cardTitle}>{title}</Text>
                    <Unicon name="chevron-forward" colorVariant="textSecondary" size={16} />
                </View>
            </View>
            <Text style={styles.cardSubTitle}>{description}</Text>
        </CutCard>
    )
}

function SectionHeader({ title, icon, iconLibrary = 'ionicon' }) {
    return (
        <View style={styles.sectionHeader}>
            <Unicon name={icon} library={iconLibrary} colorVariant="primary" size={18} />
            <Text style={styles.sectionTitle}>{title}</Text>
        </View>
    )
}

export default function AdminDashboard() {
    const router = useRouter();
    const { isWeb } = usePlatform();
    const { entitlements } = useOrganizationEntitlements();
    // Custom fields, API keys and milestones are paid-only — don't mount
    // their queries or cards on Free.
    const isFree = isFreeOrg(entitlements);
    const { data: loggedInUser } = Query.people.loggedInUser();

    if (!(loggedInUser?.isAdmin || loggedInUser?.isAccountOwner)) {
        return null;
    }

    return (
        <View style={styles.main}>
            <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer} keyboardShouldPersistTaps="handled"
>
            <View style={styles.hero}>
                <View style={styles.heroIconContainer}>
                    <Unicon name="shield-checkmark-outline" library="ionicon" colorVariant="onPrimary" size={28} />
                </View>
                <Text style={styles.heroTitle}>Admin Dashboard</Text>
                <Text style={styles.heroDescription}>
                    Manage your organization settings, data, and administrative tools.
                </Text>
            </View>

            <SectionHeader title="Organization" icon="business-outline" />
            <View style={isWeb ? styles.grid : styles.stack}>
                <DashboardCard
                    title="Organization Settings"
                    description="Update branding, timezone, protected fields, and other organization preferences."
                    icon="settings-outline"
                    onPress={() => router.push("/settings/organization")}
                    testID="organization-settings-card"
                />
                <DashboardCard
                    title="Activity Log"
                    description="View a log of recent administrative activity."
                    icon="list-outline"
                    onPress={() => router.push("/settings/organization/activity-log")}
                    testID="activity-log-card"
                />
                {!isFree && (
                    <DashboardCard
                        title="API Keys"
                        description="Create and manage developer API keys for external integrations."
                        icon="key-outline"
                        onPress={() => router.push("/settings/organization/api-keys")}
                        testID="api-keys-card"
                    />
                )}
                <DashboardCard
                    title="Online Giving"
                    description="Enable online giving. Onboard your church to accept card and ACH gifts."
                    icon="card-outline"
                    onPress={() => router.push("/settings/organization/giving")}
                    testID="online-giving-card"
                />
                {!isFree && MILESTONES_ENABLED && canViewMilestoneManagement(loggedInUser) && (
                    <DashboardCard
                        title="Milestones"
                        description="Configure milestone series, badges, and celebrations for your organization."
                        icon="trophy-outline"
                        onPress={() => router.push("/settings/organization/milestones")}
                        testID="milestones-admin-card"
                    />
                )}
                {loggedInUser?.can?.('View Billing Info') && (
                    <DashboardCard
                        title="Plan & Billing"
                        description="Manage your plan, billing details, and invoice history."
                        icon="receipt-outline"
                        onPress={() => router.push("/settings/organization/billing")}
                        testID="billing-info-card"
                    />
                )}
            </View>

            <SectionHeader title="Data Management" icon="server-outline" />
            <View style={isWeb ? styles.grid : styles.stack}>
                <DashboardCard
                    title="Import Data"
                    description="Import people and other data into your organization."
                    icon="cloud-upload-outline"
                    onPress={() => router.push("/import/people")}
                    testID="import-data-card"
                />
                {!isFree && (
                    <DashboardCard
                        title="Custom Fields"
                        description="Manage custom person fields and sections."
                        icon="reader-outline"
                        onPress={() => router.push("/customfields")}
                        testID="custom-fields-card"
                    />
                )}
                {loggedInUser?.can?.('Manage Profile Views') && (
                    <DashboardCard
                        title="Profile Views"
                        description="Create reusable profile cards that show only the fields each role needs."
                        icon="id-card-outline"
                        onPress={() => router.push("/settings/organization/profile-views")}
                        testID="profile-views-card"
                    />
                )}
                {!isFree && (
                    <DashboardCard
                        title="Small Group Types"
                        description="Create and manage types of small groups and their leader roles."
                        icon="people-roof"
                        iconLibrary="fa"
                        onPress={() => router.push("/smallgroups/types")}
                        testID="small-group-types-card"
                    />
                )}
            </View>
            </ScrollView>
        </View>
    )
}

const styles = StyleSheet.create(({ colors, fonts }) => ({
    main: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    container: {
        flex: 1,
    },
    contentContainer: {
        padding: 20,
        gap: 20,
        _web: {
            maxWidth: 1200,
            alignSelf: 'center',
            width: '100%',
        }
    },
    hero: {
        alignItems: 'center',
        paddingVertical: 20,
        gap: 12,
    },
    heroIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 16,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    heroTitle: {
        fontSize: 24,
        fontFamily: fonts.heading,
        color: colors.text,
        textAlign: 'center',
    },
    heroDescription: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
        maxWidth: 500,
        lineHeight: 20,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 15,
    },
    stack: {
        flexDirection: 'column',
        gap: 12,
    },
    card: {
        padding: 16,
        _web: {
            flex: 1,
            minWidth: 280,
            maxWidth: 'calc(33.333% - 10px)',
            // Hover lift promotes a card to its own layer, repainting the last
            // card's corner accents. Promote all cards upfront so nothing shifts.
            willChange: 'transform',
        }
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginBottom: 8,
    },
    iconContainer: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardTitleRow: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    cardTitle: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    cardSubTitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 18,
        marginLeft: 48,
    },
}))
